// Device liveness + seed status — a direct port of the Flutter helpers in
// homeScreenFunctions.dart (getStatus / getStatusCached / getInitialUpdate).
//
// WHY THE CACHE EXISTS (same reasoning as Flutter):
// the badge is ONE HTTP call PER DEVICE, and every re-render would otherwise
// re-fire all of them — a request storm on each scroll/state change. So:
//   • getStatusCached() hands back the SAME promise for a short TTL
//   • each request has a hard timeout — a badge is never worth hanging a card on
//   • anything unknown/failed/offline resolves to the blue marker
//
// Badge values:
//   '🟢'          heartbeat within ONLINE_WINDOW_MS
//   '🔴 <mins>'   last seen N minutes ago
//   '🔵'          unknown (offline, timeout, or error)
//
// The window is NOT Flutter's 60 seconds — see ONLINE_WINDOW_MS for why that
// number only ever worked by accident.

import { getDeviceStatus } from '../api/api';
import { isOffline } from '../offline/offlineMode';

export const STATUS_TTL_MS = 45000;
export const STATUS_TIMEOUT_MS = 6000;

export const ONLINE = '🟢';
export const UNKNOWN = '🔵';

const cache = new Map(); // uno -> { promise, at }

// ── Two facts from the source, not from guesswork ──────────────────────────
//
// 1. THE HEARTBEAT IS EVERY THREE MINUTES.
//    OTAOneSwitchwithButton.ino, the ESP8266 switch firmware:
//
//        if (ServerConnected && (millis() > LastIpUpdatedTime + 180000)) {
//            ProcessToServer("UpdateDeviceNetworkDetailsNew", 986);
//            ...
//        }
//
//    which the backend turns into
//    UpdateDateTimeInDevicesInstalledNew -> DevicesInstalled.LastUpdatedDateTime.
//    Note the `ServerConnected` guard: a device that loses the server STOPS
//    stamping immediately, which is exactly the signal we want.
//
// 2. THE STAMP IS IST, LABELLED UTC. functions.js, the same backend:
//
//        GetDateTimeStamp: function () {
//          return moment().utcOffset("+05:30").format("YYYY-MM-DD HH:mm:ss");
//        }
//
//    It writes IST wall-clock as a naive string; MySQL stores it zoneless and
//    the JSON serialiser appends a `Z`. So every row parses 5½ hours in the
//    FUTURE. That is by construction and fixed — not a clock that might drift.
//
// ⚠️ IF THE SERVER IS EVER FIXED TO WRITE REAL UTC, set SERVER_TZ_OFFSET_MS to
// 0. Leaving it would make every device read 5½ hours stale, i.e. permanently
// offline. One line, and it is the only line.
export const SERVER_TZ_OFFSET_MS = 5.5 * 60 * 60 * 1000;

// How stale a heartbeat may be before a device counts as offline.
//
// ⚠️ NOT 60 SECONDS. The Flutter original used 60s, but it also compared
// against the skewed stamp above, so the subtraction came out NEGATIVE and
// every device passed — the threshold was never actually exercised. Porting the
// number without the bug made green unreachable: a healthy device is on average
// 90 seconds stale and routinely three minutes.
//
// ⚠️ TWENTY, AND TEN WAS NOT MERELY TIGHT — IT WAS INSIDE THE NOISE.
//
// `LastUpdatedDateTime` is not a heartbeat. The server refreshes it in a BATCH,
// and the batch period is about the same as the old window, so a healthy device
// spends its whole life drifting between roughly 5 and 10 minutes stale and then
// snapping back. Nothing is ever fresher than five minutes.
//
// Measured across the whole estate (203 devices, UserId 1, 2026-08-09), age of
// every device carrying a heartbeat:
//
//     0–5 min      0 devices        <- nothing is EVER this fresh
//     5–8 min     27
//     8–10 min     3                <- these are 30 seconds from vanishing
//    10–60 min     0                <- a completely empty band
//    60+ min      20   +  153 stale for more than a day
//
// So a 10-minute cut fell right where the live devices sit: three of the thirty
// working devices were already within two minutes of being hidden, and when the
// batch ran late EVERY device crossed at once — which is exactly the reported
// "turn on Hide offline and they all disappear, even the ones that are online".
//
// The data also hands us the answer: between 10 and 60 minutes there is NOTHING.
// The live cluster and the dead one are separated by a 50-minute gap, so any
// threshold in it classifies perfectly. Twenty is double the worst live reading
// and still a third of the way to the nearest genuinely-offline device, which
// leaves room for one missed batch without touching the dead band.
//
// The cost is the other direction — a device that dies is still shown as online
// for up to twenty minutes. That is the right way round: the app is a remote
// control, and briefly stale beats crying wolf.
export const ONLINE_WINDOW_MS = 20 * 60 * 1000;

// Kept as a no-op so callers that used the old learned-offset mechanism do not
// break; the offset is now known from the server's source and needs no learning.
export function noteServerTimestamp() {}

export const getServerSkewMs = () => SERVER_TZ_OFFSET_MS;

// How long ago a server timestamp really was, in ms.
//
// Negative values clamp to 0: with the offset applied, "the future" only means
// the row landed between our two clock readings.
export function ageOf(ms) {
  const age = Date.now() + SERVER_TZ_OFFSET_MS - ms;
  return age < 0 ? 0 : age;
}

/** Whether a server timestamp is recent enough to call the device online. */
export const isRecent = (ms) => ageOf(ms) < ONLINE_WINDOW_MS;

function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
  ]);
}

// Uncached liveness lookup — prefer getStatusCached() from components.
export async function fetchLiveness(uniqueNumber) {
  // Offline: the server is unreachable, so the live status is unknown by
  // definition. Return blue IMMEDIATELY rather than firing a request that will
  // time out — this is per device, so a room of 10 devices otherwise sits for
  // 6 seconds doing nothing before showing the same answer.
  if (isOffline()) return UNKNOWN;
  try {
    const res = await withTimeout(getDeviceStatus(uniqueNumber), STATUS_TIMEOUT_MS);
    const rows = Array.isArray(res.data) ? res.data : [];
    const last = rows[0] && rows[0].LastUpdatedDateTime;
    if (!last) return UNKNOWN;
    const stamp = new Date(last).getTime();
    if (!Number.isFinite(stamp)) return UNKNOWN;
    const diffMs = ageOf(stamp);
    if (diffMs < ONLINE_WINDOW_MS) return ONLINE;
    return `🔴 ${Math.floor(diffMs / 60000)}`;
  } catch (e) {
    return UNKNOWN;
  }
}

// Cached liveness — call THIS from components. Returns the same promise within
// the TTL so re-renders reuse it instead of refetching.
export function getStatusCached(uniqueNumber) {
  const key = String(uniqueNumber);
  const now = Date.now();
  const hit = cache.get(key);
  if (hit && now - hit.at < STATUS_TTL_MS) return hit.promise;
  const promise = fetchLiveness(uniqueNumber);
  cache.set(key, { promise, at: now });
  return promise;
}

// Drop cached statuses so the next render refetches — wire to pull-to-refresh.
export function clearStatusCache() {
  cache.clear();
}

// ── Last-seen age, for callers that draw their own indicator ───────────────
// Same request and the same TTL discipline as getStatusCached, but returns the
// corrected AGE in ms rather than a badge string, so a component can decide its
// own thresholds and wording.
//
// Exists because GetListofDevicesbyUserId returns no timestamp at all: a tile
// fed from that list has nothing on the row to work from, and asking the status
// endpoint is the only way to show anything.
const ageCache = new Map(); // uno -> { promise, at }

export async function fetchLastSeenAge(uniqueNumber) {
  if (isOffline()) return null;
  try {
    const res = await withTimeout(getDeviceStatus(uniqueNumber), STATUS_TIMEOUT_MS);
    const rows = Array.isArray(res.data) ? res.data : [];
    const last = rows[0] && rows[0].LastUpdatedDateTime;
    if (!last) return null;
    const stamp = new Date(last).getTime();
    return Number.isFinite(stamp) ? ageOf(stamp) : null;
  } catch (e) {
    return null;
  }
}

export function getLastSeenCached(uniqueNumber) {
  const key = String(uniqueNumber);
  const now = Date.now();
  const hit = ageCache.get(key);
  if (hit && now - hit.at < STATUS_TTL_MS) return hit.promise;
  const promise = fetchLastSeenAge(uniqueNumber);
  ageCache.set(key, { promise, at: now });
  return promise;
}

// Seed a control screen's state from the last status the server recorded.
// Flutter's getInitialUpdate(): take Status, append '#', then keep the substring
// between ']' and '#'. Returns '' on any failure — control screens treat '' as
// "state unknown → show blue and wait for MQTT". Never return a placeholder
// word here: a non-empty 'Unknown' used to poison the seed parsers.
export async function fetchSeedStatus(uniqueNumber) {
  // Offline: no cached live status exists (status arrives over MQTT), so seed
  // empty at once instead of delaying the control screen behind a doomed
  // request. The screen shows "unknown" and updates when a status arrives.
  if (isOffline()) return '';
  try {
    const res = await withTimeout(getDeviceStatus(uniqueNumber), STATUS_TIMEOUT_MS);
    const rows = Array.isArray(res.data) ? res.data : [];
    const raw = rows[0] && rows[0].Status;
    if (raw == null) return '';
    const s = `${raw}#`;
    return s.substring(s.indexOf(']') + 1, s.indexOf('#'));
  } catch (e) {
    return '';
  }
}
